import axios from "axios";
import process from "process";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CountryFlag from "../../components/CountryFlag";
import Translations from "../../components/MultiLanguage";
import Languages from "../../components/languages";

if (process.env.NODE_ENV === "development") {
  axios.defaults.baseURL = "http://localhost:5000";
} else if (window.location.hostname === "localhost") {
  axios.defaults.baseURL = "http://localhost:5000";
}

function SignUp() {
  const navigateTo = useNavigate();
  const location = useLocation();
  const { account_id } = location.state || {};
  const [isEmpfehlio, setEmpfehlio] = useState(false);
  const [selected, setSelected] = useState(Languages[1]);
  const [languageMenu, setLanguageMenu] = useState(false);
  const [phrases, setPhrases] = useState(Translations[selected.locale]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLanguageChange = (selectedLocale) => {
    setSelected(selectedLocale);
    setLanguageMenu(false);
    setPhrases(Translations[selectedLocale.locale]);
  };

  useEffect(() => {
    if (window.location.hostname === "empfehlio.ekomiapps.de") {
      setEmpfehlio(true);
      setPhrases(Translations["de"]);
    }
  }, []);

  useEffect(() => {
    // Handle missing account_id
    if (!account_id) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        account_id: phrases.AccountIdMissing,
      }));
    }
  }, [account_id, phrases.AccountIdMissing]);

  // Handle input change without validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update form data
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Validate the entire form on submit
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = phrases.FirstNameRequired;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = phrases.LastNameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = phrases.EmailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = phrases.InvalidEmail;
    }

    if (!formData.password) {
      newErrors.password = phrases.PasswordRequired;
    } else if (formData.password.length < 8) {
      newErrors.password = phrases.PasswordLength;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = phrases.PasswordMismatch;
    }

    // Check for account_id
    if (!account_id) {
      newErrors.account_id = phrases.AccountIdMissing;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Submit the form data to the server, including account_id
        await axios.post(
          "/api/sign-up-account",
          { ...formData, account_id, isEmpfehlio },
          { headers: { "X-Custom-Origin": "https://truzzer.com/" } }
        );
        // Redirect to login page or show success message
        navigateTo("/login", {
          state: {
            email: formData.email,
            account_id: account_id,
            successSingUp: true,
          },
        });
      } catch (error) {
        setErrors({
          submit: error.response?.data?.message || phrases.SignUpError,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="flex items-center min-h-screen">
      {!isEmpfehlio && (
        <span className="absolute top-1 left-1 cursor-pointer text-white z-50">
          <div className="relative">
            <button
              onClick={() => setLanguageMenu(!languageMenu)}
              className="relative w-full cursor-default rounded-md pr-10 text-left text-gray-900 sm:text-sm sm:leading-6"
            >
              <span className="flex items-center">
                <CountryFlag countryCode={selected.countryCode} />
                <span className="ml-3 block truncate text-gray-900">
                  {selected.country}
                </span>
              </span>
            </button>

            {languageMenu && (
              <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white py-1 text-black shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {Languages.slice(1).map((language) => (
                  <div
                    key={language.id}
                    onClick={() => handleLanguageChange(language)}
                    className="cursor-default select-none relative p-2"
                  >
                    <div className="flex items-center">
                      <CountryFlag countryCode={language.countryCode} />
                      <span className="ml-3 block truncate">
                        {language.country}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </span>
      )}
      <div className="flex-1 flex-col justify-center px-6 py-12 lg:px-8 justify-items-center">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            {phrases.SignUpDescription}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* First Name */}
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {phrases.FirstName}:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    errors.firstName ? "ring-red-500" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.firstName}
                  </p>
                )}
              </div>
            </div>

            {/* Last Name */}
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {phrases.LastName}:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    errors.lastName ? "ring-red-500" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {phrases.Email}:
              </label>
              <div className="mt-2">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    errors.email ? "ring-red-500" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {phrases.Password}:
              </label>
              <div className="mt-2">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    errors.password ? "ring-red-500" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {phrases.PasswordConfirm}:
              </label>
              <div className="mt-2">
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    errors.confirmPassword ? "ring-red-500" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
                />
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Account ID Missing Error */}
            {errors.account_id && (
              <p className="text-sm text-red-600">{errors.account_id}</p>
            )}

            {errors.submit && (
              <p className="text-sm text-red-600">{errors.submit}</p>
            )}

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex w-full px-2 justify-center rounded-md ${
                  !isSubmitting
                    ? "bg-green-600 hover:bg-green-500"
                    : "bg-gray-400 cursor-not-allowed"
                } px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm`}
              >
                {isSubmitting ? phrases.SigningUp : phrases.SignUp}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
